import { BuyerShortagesPanel } from './BuyerShortagesPanel';
import { useAuthStore } from '../../stores/auth';
import { PurchaseRequestForm } from './PurchaseRequestForm';
import { StatCard, UrgencyBadge, StatusBadge } from './PurchaseRequestBadges';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardList, Plus, X, Search, Filter, Loader2, ChevronLeft, ChevronRight,
  CheckCircle2, Clock, XCircle, ArrowRightCircle, Ban, FileCheck,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

const TRANSFERS_LIVE = true; // Phase 2 plan 06: /warehouse/transfers is live

// ==================== Types ====================

interface PurchaseRequestStats {
  pending: number;
  approved: number;
  converted: number;
  rejected: number;
}

interface PurchaseRequest {
  id: string;
  number: string;
  productName: string;
  product?: { id: string; name: string; sku: string };
  quantity: number;
  unit: string;
  urgency: string;
  status: string;
  requestedBy: string;
  requesterName?: string | null;
  raisedFromLabel?: string;
  stock?: { factory: number; warehouse: number };
  suggestion?: { action: 'issue_from_other_location' | 'raise_purchase'; from: 'FACTORY' | 'WAREHOUSE' | null; sentence: string };
  hasActiveContract: boolean;
  estimatedRate: number | null;
  createdAt: string;
  reason: string;
  notes: string;
}

interface ProductOption {
  id: string;
  name: string;
  sku: string;
}

// ==================== Component ====================

export function PurchaseRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const location = useLocation();
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [stats, setStats] = useState<PurchaseRequestStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // New Request Modal
  const [showModal, setShowModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [newQty, setNewQty] = useState('');
  const [newUnit, setNewUnit] = useState('pcs');
  const [newUrgency, setNewUrgency] = useState('medium');
  const [newReason, setNewReason] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const preset = location.state;
    if (preset?.productId && preset?.productName && Number(preset.quantity) > 0) {
      setSelectedProduct({ id: preset.productId, name: preset.productName, sku: '' });
      setNewQty(String(preset.quantity)); setNewUnit(preset.unit || 'pcs'); setNewReason(preset.reason || ''); setShowModal(true);
    }
  }, [location.key]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/purchase-requests/stats');
      if (!['pending','approved','converted','rejected'].every(k => Number.isFinite(data[k]))) throw new Error('Invalid request counts');
      setStats(data);
    } catch { setStats(null); setError('Could not load purchase request counts.'); }

  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (urgencyFilter !== 'all') params.urgency = urgencyFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get('/purchase-requests', { params });
      setRequests(data.data || []);
      setTotal(data.total ?? data.pagination?.total ?? 0);
      setTotalPages(data.totalPages ?? data.pagination?.totalPages ?? 1);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load purchase requests');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, urgencyFilter, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { setPage(1); }, [statusFilter, urgencyFilter, search]);

  const searchProducts = async (q: string) => {
    setProductSearch(q);
    if (q.length < 2) { setProductOptions([]); return; }
    try {
      const { data } = await api.get('/products', { params: { search: q, limit: 10 } });
      setProductOptions((data.data || []).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku })));
    } catch { setProductOptions([]); }
  };

  const createRequest = async () => {
    if (!selectedProduct || !newQty) return;
    setSubmitting(true);
    try {
      await api.post('/purchase-requests', {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        quantity: Number(newQty),
        unit: newUnit,
        urgency: newUrgency,
        reason: newReason,
        notes: newNotes,
      });
      setShowModal(false);
      resetModal();
      fetchStats();
      fetchRequests();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetModal = () => {
    setProductSearch('');
    setProductOptions([]);
    setSelectedProduct(null);
    setNewQty('');
    setNewUnit('pcs');
    setNewUrgency('medium');
    setNewReason('');
    setNewNotes('');
  };

  if (loading && !requests.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error && !requests.length) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button onClick={fetchRequests} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Purchase Requests</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage procurement requests from manufacturing and inventory
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          New Request
        </button>
      </div>

      {/* Stats Bar */}
      {user?.role?.name === 'buyer' && <BuyerShortagesPanel onRaise={row => { setSelectedProduct({id:row.productId,name:row.name,sku:''});setNewQty(String(row.short));setNewUnit(row.unit);setNewReason(`Production shortage: ${row.neededFor.join(', ')}`);setShowModal(true); }} />}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Clock} label="Pending" value={stats.pending} color="yellow" />
          <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} color="blue" />
          <StatCard icon={ArrowRightCircle} label="Converted" value={stats.converted} color="green" />
          <StatCard icon={XCircle} label="Rejected" value={stats.rejected} color="red" />
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by product name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="converted">Converted</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={urgencyFilter}
          onChange={(e) => setUrgencyFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Urgency</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">PR #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Product</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Qty</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Unit</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Urgency</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Requested By</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Contract</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Est. Rate</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Raised from</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">In stock</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-12 text-center text-gray-400 dark:text-gray-500">
                    No purchase requests found
                  </td>
                </tr>
              ) : (
                requests.map((pr) => (
                  <tr
                    key={pr.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/purchase-requests/${pr.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-blue-600 dark:text-blue-400">{pr.number}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{pr.productName}</td>
                    <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{pr.quantity}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{pr.unit}</td>
                    <td className="px-4 py-3 text-center"><UrgencyBadge urgency={pr.urgency} /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={pr.status} /></td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{pr.requesterName ?? pr.requestedBy}</td>
                    <td className="px-4 py-3 text-center">
                      {pr.hasActiveContract ? (
                        <FileCheck className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {pr.estimatedRate != null ? formatINR(pr.estimatedRate) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{formatDate(pr.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{pr.raisedFromLabel ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{pr.stock ? `F ${pr.stock.factory} · W ${pr.stock.warehouse}` : '—'}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {pr.status === 'pending' && pr.suggestion?.action === 'issue_from_other_location' ? (
                        TRANSFERS_LIVE ? (
                          <Link to={`/warehouse/transfers?productId=${pr.product?.id ?? ''}&qty=${pr.quantity}&from=${pr.suggestion.from}`} title={pr.suggestion.sentence} className="text-xs font-medium text-blue-600 hover:underline">
                            {`Issue from ${pr.suggestion.from === 'WAREHOUSE' ? 'Warehouse' : 'Factory'}`}
                          </Link>
                        ) : (
                          <span title={pr.suggestion.sentence} className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
                            {`Issue from ${pr.suggestion.from === 'WAREHOUSE' ? 'Warehouse' : 'Factory'}`}
                          </span>
                        )
                      ) : pr.status === 'pending' ? (
                        <Link to="/purchase-orders?view=release" className="text-xs font-medium text-blue-600 hover:underline">To release queue</Link>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <PurchaseRequestForm showModal={showModal} setShowModal={setShowModal} resetModal={resetModal} selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} productSearch={productSearch} searchProducts={searchProducts} productOptions={productOptions} setProductOptions={setProductOptions} setProductSearch={setProductSearch} newQty={newQty} setNewQty={setNewQty} newUnit={newUnit} setNewUnit={setNewUnit} newUrgency={newUrgency} setNewUrgency={setNewUrgency} newReason={newReason} setNewReason={setNewReason} newNotes={newNotes} setNewNotes={setNewNotes} submitting={submitting} createRequest={createRequest} />
    </div>
  );
}
